/**
 * Supabase client factory.
 *
 * Builds a client configured for the browser:
 *   - persists session in localStorage (so returning players stay
 *     signed in), auto-refreshes tokens, and picks up magic-link /
 *     OAuth tokens from the URL on return redirects — *when running
 *     standalone*.
 *   - in an iframe, suppresses persistSession so the embedded game
 *     doesn't hold its own copy of the launcher's tokens. Identity is
 *     adopted from the parent via the `vibecade:session` postMessage
 *     handshake (decision #68), so iframe-side localStorage adds no
 *     value and creates a real bug — the iframe and the launcher both
 *     end up holding the same one-use refresh token, and whoever
 *     refreshes second gets `Invalid Refresh Token: Already Used` from
 *     Supabase. The error is recoverable (the handshake supplies a
 *     fresh session) but loud in devtools and confusing for game devs
 *     during integration.
 *
 * Storage key stays namespaced for the standalone case so two SDK
 * instances on the same origin (a launcher and the game it embeds) don't
 * fight over one slot. It is namespaced by `authScope`, which defaults to
 * `gameId` — identical behaviour for every consumer that does not set it.
 *
 * The exception the option exists for is a game that owns several game
 * ids, one per leaderboard. There the clients are the SAME player and must
 * share a session; on the default they would each sign in as a new
 * anonymous player instead. Sharing the key also makes their token
 * refreshes safe, since auth-js serializes refresh on `lock:${storageKey}`
 * through the Web Locks API. It does NOT make first sign-in safe — that
 * is a check-then-create spanning two calls, which auth.ts serializes per
 * scope itself.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { ResolvedConfig } from './context';
import { SDK_VERSION } from './version';

export function isInIframe(): boolean {
  // SSR / non-browser hosts: not an iframe.
  if (typeof window === 'undefined') return false;
  // window.parent === window when not embedded; cross-origin reads of
  // window.parent properties would throw, but identity comparison is
  // safe even across origins.
  return window.parent !== window;
}

export function createSupabaseClient(config: ResolvedConfig): SupabaseClient {
  const inIframe = isInIframe();
  return createClient(config.apiUrl, config.anonKey, {
    auth: {
      // In-iframe: the parent owns the canonical session, the SDK
      // adopts it via postMessage on every load. Persisting locally
      // races the parent's token rotation. Standalone: persist so anon
      // identity survives a reload.
      persistSession: !inIframe,
      // In-iframe the launcher is the SOLE refresh authority: it holds
      // the only real refresh token (in its own origin) and pushes a
      // freshly-rotated access token to the game over the
      // `vibecade:session` postMessage channel whenever its own session
      // refreshes (see launcher Play.tsx + auth.ts adoption path). The
      // iframe therefore must NOT attempt its own refresh — it carries
      // only a non-functional placeholder refresh token, and an
      // auto-refresh attempt with that would 401 and noisily clear the
      // session. Standalone (non-iframe) keeps auto-refresh so a
      // returning anonymous player stays signed in across a reload.
      autoRefreshToken: !inIframe,
      // Magic-link / OAuth callback redirects always land on the
      // launcher origin, never on a game iframe — so this is only
      // useful standalone.
      detectSessionInUrl: !inIframe,
      storageKey: `vibecade-auth-${config.authScope}`,
    },
    global: {
      headers: {
        'x-vibecade-sdk': SDK_VERSION,
      },
    },
  });
}

/**
 * The client every PostgREST read and write goes through, holding no session
 * of its own. It takes its token from `getToken` on each request.
 *
 * A second client exists because of the embedded game. There the session
 * carries IFRAME_PLACEHOLDER_REFRESH_TOKEN (see auth.ts), and supabase-js
 * attaches a token to every query by calling `auth.getSession()`. Within 90s
 * of expiry getSession() refreshes, whatever autoRefreshToken says. The
 * placeholder is refused, and auth-js answers a refused refresh by REMOVING
 * the session: one leaderboard read near expiry signed the player out, and
 * the score they submitted next failed as NOT_INITIALIZED. With `accessToken`
 * set, supabase-js asks `getToken` instead and never reaches that refresh.
 *
 * The `accessToken` option arrived in supabase-js 2.45.0, and the peer range
 * starts at 2.42.0. An older client ignores the option and builds an auth
 * client of its own, so the `auth` block below keeps that one inert (in
 * memory, no refresh, no URL parsing, its own storage key), and the probe
 * hands back `fallback` so reads keep today's behaviour on that version.
 *
 * Built only after AuthModule exists: supabase-js calls `getToken` once while
 * constructing, to prime realtime.
 */
export function createDataClient(
  config: ResolvedConfig,
  getToken: () => Promise<string | null>,
  fallback: SupabaseClient,
): SupabaseClient {
  const client = createClient(config.apiUrl, config.anonKey, {
    accessToken: getToken,
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: `vibecade-data-${config.authScope}`,
    },
    global: {
      headers: {
        'x-vibecade-sdk': SDK_VERSION,
      },
    },
  });
  // With the option honoured, `auth` is a proxy that throws on any property.
  try {
    void (client.auth as unknown as Record<string, unknown>).getSession;
  } catch {
    return client;
  }
  return fallback;
}
